import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import SelectTextField from '../../molecules/SelectTextField';
import ConfigureCriteriaForm from './shared/ConfigureCriteriaForm';
import { collateralApi } from '../../../services/collateralApi';

interface Props {
    creditOperationId: string;
    operationCode: string;
    assignorName: string;
    assignorTaxId: string;
    totalReceivables: number;
    totalAmount: number;
    receivables?: { receivable_id: string; nfe_key?: string; receivable_number?: string; title_number?: string; debtor_cpf_cnpj: string; debtor_name?: string; debtor_email?: string; debtor_phone?: string; amount?: number; emission_date?: string; due_date?: string; city?: string; state?: string }[];
    hideCriteriaAndIntensity?: boolean;
}

const INITIAL_VALIDATION_CONFIG: Record<string, string> = {
    validation_xml: 'DO_NOT_VALIDATE', validation_invoice_pdf: 'DO_NOT_VALIDATE',
    validation_receipt_stub: 'DO_NOT_VALIDATE', validation_accepted_bill: 'DO_NOT_VALIDATE',
    validation_contact: 'DO_NOT_VALIDATE', validation_purchase_contract: 'DO_NOT_VALIDATE',
    validation_sefaz: 'DO_NOT_VALIDATE', validation_debtor_notification: 'DO_NOT_VALIDATE',
};

const CRITERIA_KEY_MAP: Record<string, string> = {
    validation_xml: 'XML', validation_invoice_pdf: 'INVOICE_PDF',
    validation_receipt_stub: 'RECEIPT_STUB', validation_contact: 'DRAWEE_CONFIRMATION',
    validation_sefaz: 'SEFAZ', validation_accepted_bill: 'ACCEPTED_BILL',
    validation_purchase_contract: 'PURCHASE_CONTRACT', validation_debtor_notification: 'DEBTOR_NOTIFICATION',
};

const ValidationConfigSection = ({
    creditOperationId, operationCode, assignorName, assignorTaxId, totalReceivables, totalAmount, receivables,
    hideCriteriaAndIntensity,
}: Props) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [sectionData, setSectionData] = useState<any>(null);

    // Form state
    const [operationType, setOperationType] = useState('CESSAO');
    const [obligationType, setObligationType] = useState('WITHOUT_RECOURSE');
    const [traderName, setTraderName] = useState('');
    const [validationConfig, setValidationConfig] = useState<Record<string, string>>({ ...INITIAL_VALIDATION_CONFIG });
    const [analysisCriteria, setAnalysisCriteria] = useState<'Completa' | 'Amostragem'>('Completa');
    const [sampledTitles, setSampledTitles] = useState(1);

    const loadSection = useCallback(async () => {
        setHasError(false);
        try {
            const data = await collateralApi.getVerificationConfigSection(creditOperationId);
            setSectionData(data);
            if (data.exists) {
                setOperationType(data.operationType || 'CESSAO');
                setObligationType(data.obligationType || 'WITHOUT_RECOURSE');
                setTraderName(data.traderName || '');
                setAnalysisCriteria(data.analysisMode === 'SAMPLE' ? 'Amostragem' : 'Completa');
                setSampledTitles(data.sampleCount || 1);
                const config = { ...INITIAL_VALIDATION_CONFIG };
                Object.entries(data.validationConfig || {}).forEach(([k, v]) => { config[k] = v as string; });
                setValidationConfig(config);
            }
        } catch {
            setHasError(true);
            setSectionData(null);
        } finally {
            setIsLoading(false);
        }
    }, [creditOperationId]);

    useEffect(() => { loadSection(); }, [loadSection]);

    const buildCriteria = () => {
        const criteria: { criteria_type: string; timing: string }[] = [];
        Object.entries(validationConfig).forEach(([key, val]) => {
            const type = CRITERIA_KEY_MAP[key];
            if (type && val && val !== 'DO_NOT_VALIDATE') {
                criteria.push({ criteria_type: type, timing: val });
            }
        });
        return criteria;
    };

    const handleCreate = async () => {
        const criteria = buildCriteria();
        if (criteria.length === 0) { toast.warn('Selecione ao menos um critério.'); return; }
        setIsSaving(true);
        try {
            const receivable_ids = (receivables || []).map(r => r.receivable_id);
            await collateralApi.createVerificationConfig({
                credit_operation_id: creditOperationId,
                criteria,
                receivable_ids,
                receivables: receivables || [],
                analysis_mode: analysisCriteria === 'Amostragem' ? 'SAMPLE' : 'COMPLETE',
                sample_count: analysisCriteria === 'Amostragem' ? sampledTitles : undefined,
                operation_code: operationCode,
                operation_type: operationType,
                obligation_type: obligationType,
                assignor_name: assignorName,
                assignor_tax_id: assignorTaxId,
                total_receivables: totalReceivables,
                total_amount: totalAmount,
                trader_name: traderName,
            });
            toast.success('Enviado para validação com sucesso!');
            loadSection();
        } catch {
            toast.error('Erro ao enviar para validação.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = async () => {
        const criteria = buildCriteria();
        if (criteria.length === 0) { toast.warn('Selecione ao menos um critério.'); return; }
        setIsSaving(true);
        try {
            await collateralApi.editVerificationCriteria(sectionData.id, criteria, {
                operationType, obligationType, traderName,
                analysisMode: analysisCriteria === 'Amostragem' ? 'SAMPLE' : 'COMPLETE',
                sampledTitles: analysisCriteria === 'Amostragem' ? sampledTitles : undefined,
            });
            toast.success('Configuração atualizada!');
            loadSection();
        } catch {
            toast.error('Erro ao atualizar configuração.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading && !sectionData) {
        return (
            <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <div className="animate-pulse space-y-3">
                    <div className="h-4 w-48 bg-gray-200 dark:bg-slate-600 rounded" />
                    <div className="grid grid-cols-3 gap-3">
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                        <div className="h-9 bg-gray-200 dark:bg-slate-600 rounded" />
                    </div>
                </div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="mb-6 bg-red-50 dark:bg-red-900/10 border-2 border-red-300 dark:border-red-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">Falha ao carregar configuração de validação</h3>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">Não foi possível obter os dados. Verifique sua conexão e tente novamente.</p>
                    </div>
                    <button onClick={() => { setIsLoading(true); loadSection(); }}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded">
                        Recarregar
                    </button>
                </div>
            </div>
        );
    }

    if (!sectionData) {
        return null;
    }

    const exists = sectionData?.exists;
    const canEdit = sectionData?.canEdit;
    const disabled = exists && !canEdit;
    const criteriaWithProgress: string[] = sectionData?.criteriaWithProgress || [];

    return (
        <div className="mb-6 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Configuração do Lote para Validação</h3>
                    {exists && !canEdit && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                            ✓ Em processo de validação
                        </span>
                    )}
                    {exists && canEdit && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                            Configurado
                        </span>
                    )}
                </div>
                {!exists && (
                    <button type="button" onClick={handleCreate} disabled={isSaving}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded disabled:opacity-50">
                        {isSaving ? 'Salvando...' : 'Enviar para Validação'}
                    </button>
                )}
                {exists && canEdit && (
                    <button type="button" onClick={handleEdit} disabled={isSaving}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded disabled:opacity-50">
                        {isSaving ? 'Salvando...' : 'Alterar Validação'}
                    </button>
                )}
            </div>

            <ConfigureCriteriaForm
                operationSummary={{ operationCode, assignorName, totalAmount, totalReceivables }}
                operationType={operationType} setOperationType={setOperationType}
                obligationType={obligationType} setObligationType={setObligationType}
                traderName={traderName} setTraderName={setTraderName}
                validationConfig={validationConfig} setValidationConfig={setValidationConfig}
                analysisCriteria={analysisCriteria} setAnalysisCriteria={setAnalysisCriteria}
                sampledTitles={sampledTitles} setSampledTitles={setSampledTitles}
                disabled={disabled || isSaving}
                draweeCannotBeDisabled={exists && !canEdit}
                lockedCriteria={criteriaWithProgress}
                hideCriteriaAndIntensity={hideCriteriaAndIntensity}
                compact
            />
        </div>
    );
};

export default ValidationConfigSection;
